import { createColumnHelper } from '@tanstack/react-table';
import { useState } from 'react';

import { OnboardingFileView } from '@ndvr/customer-api';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { CondensedTable, DateTimeDisplay, PageSpinner } from 'widgets';

import { FilePreviewDialog } from '../HouseholdOnboardingTab/FilePreviewDialog';
import {
    onboardingFilesArchiveUrl,
    onboardingFileUrl,
    useOnboardingFiles,
} from '../HouseholdOnboardingTab/onboarding-tab-queries';

const columnHelper = createColumnHelper<OnboardingFileView>();

export const OnboardingFilesSection = ({ householdGUID }: { householdGUID: string }) => {
    const { data: files = [], isPending, isError } = useOnboardingFiles(householdGUID);
    const [previewFile, setPreviewFile] = useState<OnboardingFileView | null>(null);
    const columns = [
        columnHelper.accessor('fileName', { header: 'Name' }),
        columnHelper.accessor('createdAt', {
            header: 'Uploaded At',
            cell: ({ getValue }) => <DateTimeDisplay dateString={getValue()} />,
        }),
        columnHelper.display({
            id: 'actions',
            cell: ({ row: { original: file } }) => (
                <div className="flex gap-3">
                    <Button variant="link" onClick={() => setPreviewFile(file)}>
                        Open
                    </Button>
                    <Button asChild variant="link">
                        <a href={onboardingFileUrl(householdGUID, file)} download>
                            Download
                        </a>
                    </Button>
                </div>
            ),
        }),
    ];

    return (
        <section aria-label="Onboarding Files" className="flex flex-col gap-6 p-5">
            <div className="flex items-center justify-between gap-4">
                <h2 className="text-cocoa-1000 text-xl">Onboarding Files</h2>
                {files.length > 0 && (
                    <Button asChild variant="outline" size="sm">
                        <a href={onboardingFilesArchiveUrl(householdGUID)} download>
                            Download all
                        </a>
                    </Button>
                )}
            </div>
            {isPending ? (
                <PageSpinner />
            ) : isError ? (
                <p role="alert">Could not load onboarding files.</p>
            ) : files.length === 0 ? (
                <p className="text-muted-foreground text-sm">No onboarding files uploaded.</p>
            ) : (
                <Card>
                    <CondensedTable data={files} columns={columns} />
                </Card>
            )}
            {previewFile && (
                <FilePreviewDialog
                    householdGUID={householdGUID}
                    file={previewFile}
                    onClose={() => setPreviewFile(null)}
                />
            )}
        </section>
    );
};
